<template>
  <div class="container">
    <div class="columns box-title is-12">
      <div class="column is-6">
        <h4 class="has-text-left">
          <i class="fal fa-user-clock"></i>
          {{ $t('inactiveCustomers.page_title') }}
          <span class="countTotal">({{ totalRows }})</span>
        </h4>
      </div>
      <div class="column is-6">
        <div style="float: right">
          <vs-button
              flat
              class="form-btn-loading"
              style="margin-top: 0"
              :loading="requesting"
              :disabled="requesting || loading"
              @click="exportExcel"
          >
            <i class="fal fa-file-export"></i>&nbsp;{{ $t('inactiveCustomers.export_button') }}
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-12-mobile is-4-tablet is-3-desktop">
          <div class="control">
            <label class="label">{{ $t('inactiveCustomers.filters.days') }}:</label>
            <div class="field has-addons">
              <p class="control is-expanded">
                <input
                    v-model.number="form.days"
                    class="input"
                    type="number"
                    min="1"
                    :max="maxDays"
                    @change="onFilterChange"
                    @keyup.enter="onFilterChange"
                />
              </p>
              <p class="control">
                <span class="button is-static">{{ $t('inactiveCustomers.filters.days_unit') }}</span>
              </p>
            </div>
            <div class="presets">
              <a
                  v-for="preset in presets"
                  :key="preset"
                  class="tag"
                  :class="{'is-primary': form.days === preset}"
                  @click="setDays(preset)"
              >{{ preset }}</a>
            </div>
          </div>
        </div>
        <div class="column is-12-mobile is-4-tablet is-3-desktop">
          <div class="control">
            <label class="label">{{ $t('inactiveCustomers.filters.province') }}:</label>
            <div class="select is-fullwidth">
              <select v-model="form.province_id" @change="onFilterChange">
                <option value="">{{ $t('inactiveCustomers.filters.all_provinces') }}</option>
                <option v-for="p in provinces" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-12-mobile is-4-tablet is-2-desktop">
          <div class="control">
            <label class="label">{{ $t('inactiveCustomers.filters.customer_type') }}:</label>
            <div class="select is-fullwidth">
              <select v-model="form.customer_type" @change="onFilterChange">
                <option value="">{{ $t('inactiveCustomers.filters.all_types') }}</option>
                <option v-for="t in customerTypes" :key="t" :value="t">{{ $t('inactiveCustomers.customer_type.' + t) }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop">
          <div class="control">
            <label class="label">{{ $t('inactiveCustomers.filters.search') }}:</label>
            <input
                v-model="form.q"
                class="input"
                type="text"
                :placeholder="$t('inactiveCustomers.filters.search_placeholder')"
                @input="onSearchInput"
            />
          </div>
        </div>
        <div class="column is-12-mobile is-6-tablet is-2-desktop never-col">
          <label class="checkbox">
            <input v-model="form.include_never_opened" type="checkbox" @change="onFilterChange"/>
            {{ $t('inactiveCustomers.filters.include_never_opened') }}
          </label>
        </div>
      </div>
      <p class="hint">
        {{ $t('inactiveCustomers.cutoff_hint', {days: appliedDays, cutoff: cutoffLabel}) }}
      </p>
    </div>

    <!-- Preview -->
    <div class="center">
      <div v-if="loading" class="state-panel">
        <Loading/>
        <p>{{ $t('inactiveCustomers.loading') }}</p>
      </div>

      <div v-else-if="errorMessage" class="state-panel error-panel">
        <p>{{ errorMessage }}</p>
        <vs-button flat danger @click="loadList(currentPage)">{{ $t('inactiveCustomers.retry') }}</vs-button>
      </div>

      <div v-else-if="rows.length === 0" class="state-panel">
        <p>{{ $t('inactiveCustomers.empty_state') }}</p>
      </div>

      <vs-table v-else>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('inactiveCustomers.table_columns.customer_code') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.name') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.tel') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.customer_type') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.address') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.registered_at') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.last_active_at') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.days_inactive') }}</vs-th>
            <vs-th>{{ $t('inactiveCustomers.table_columns.status') }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr v-for="row in rows" :key="row.customer_row_id">
            <vs-td>{{ row.customer_code || placeholder }}</vs-td>
            <vs-td>{{ fullName(row) }}</vs-td>
            <vs-td>{{ row.customer_tel || placeholder }}</vs-td>
            <vs-td>{{ row.customer_type_label || placeholder }}</vs-td>
            <vs-td>{{ address(row) }}</vs-td>
            <vs-td>{{ formatDate(row.customer_created_at) }}</vs-td>
            <vs-td>{{ row.never_opened ? placeholder : formatDate(row.last_active_at) }}</vs-td>
            <vs-td>{{ row.never_opened ? placeholder : row.days_inactive }}</vs-td>
            <vs-td>
              <span class="tag" :class="row.never_opened ? 'is-warning' : 'is-danger is-light'">
                {{ row.never_opened ? $t('inactiveCustomers.status.never_opened') : $t('inactiveCustomers.status.inactive') }}
              </span>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <pagination
              v-if="paginator && paginator.last_page > 1"
              :pagination="paginator"
              :offset="10"
              @paginate="changePage"
          ></pagination>
        </template>
      </vs-table>
    </div>

    <!-- Export files -->
    <div class="box exports-box">
      <div class="exports-title">
        <h5><i class="fal fa-file-excel"></i> {{ $t('inactiveCustomers.exports.title') }}</h5>
        <vs-button flat size="small" :loading="exportsLoading" @click="fetchExports">
          <i class="fal fa-sync-alt"></i> {{ $t('inactiveCustomers.exports.refresh') }}
        </vs-button>
      </div>
      <p class="hint">{{ $t('inactiveCustomers.exports.hint') }}</p>
      <table class="table is-fullwidth is-hoverable is-striped">
        <thead>
        <tr>
          <th>{{ $t('inactiveCustomers.exports.file') }}</th>
          <th>{{ $t('inactiveCustomers.exports.status') }}</th>
          <th>{{ $t('inactiveCustomers.exports.created_at') }}</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="f in exports" :key="f.id">
          <td><span class="reference">{{ f.name }}</span></td>
          <td><span class="tag" :class="statusClass(f.status)">{{ statusLabel(f.status) }}</span></td>
          <td><small>{{ formatDateTime(f.created_at) }}</small></td>
          <td>
            <vs-button v-if="Number(f.status) === 1" flat size="small" :loading="downloadingId === f.id" @click="download(f.id)">
              <i class="fal fa-download"></i> {{ $t('inactiveCustomers.exports.download') }}
            </vs-button>
            <span v-else-if="Number(f.status) === 2" class="has-text-danger"><i class="fal fa-times"></i> {{ $t('inactiveCustomers.exports.failed') }}</span>
            <span v-else class="has-text-grey"><i class="fal fa-spinner fa-spin"></i> {{ $t('inactiveCustomers.exports.processing') }}</span>
          </td>
        </tr>
        </tbody>
      </table>
      <p v-if="!exports.length && !exportsLoading" class="has-text-centered has-text-grey py-3">
        {{ $t('inactiveCustomers.exports.empty') }}
      </p>
    </div>
  </div>
</template>

<script>
import Loading from '../../../Loading';
import pagination from '../../../../Plugins/Paginations/Pagination';
import {
  downloadExport,
  errorMessage,
  fetchInactiveCustomers,
  fetchProvinces,
  listExports,
  requestExport
} from '@/services/inactiveCustomerService';

const REFRESH_MS = 8000;
// ~20 minutes of auto-refresh while an export is processing, then stop (manual ໂຫຼດໃໝ່ still works).
// Sized above the measured full-size run (772k rows ≈ 10.5 min) and below the job's 25-min timeout.
const MAX_REFRESH = 150;
const DEFAULT_DAYS = 90;
const MAX_DAYS = 3650;
const PER_PAGE = 20;

export default {
  name: 'InactiveCustomers',
  components: {Loading, pagination},
  data() {
    return {
      form: {days: DEFAULT_DAYS, include_never_opened: true, province_id: '', customer_type: '', q: ''},
      appliedDays: DEFAULT_DAYS,
      cutoff: null,
      presets: [30, 60, 90, 180, 365],
      customerTypes: ['general', 'pre_customer', 'vip', 'pre_company', 'company'],
      maxDays: MAX_DAYS,
      provinces: [],
      rows: [],
      paginator: null,
      loading: false,
      errorMessage: '',
      listRequestId: 0,
      searchDebounceTimer: null,
      exports: [],
      exportsLoading: false,
      requesting: false,
      downloadingId: null,
      refreshTimer: null,
      refreshCount: 0,
      alive: true,
    };
  },
  computed: {
    placeholder() {
      return this.$t('inactiveCustomers.placeholder');
    },
    totalRows() {
      return this.paginator && this.paginator.total ? this.paginator.total : this.rows.length;
    },
    currentPage() {
      return this.paginator && this.paginator.current_page ? this.paginator.current_page : 1;
    },
    cutoffLabel() {
      return this.cutoff ? this.formatDate(this.cutoff) : this.placeholder;
    },
  },
  methods: {
    validDays() {
      // Number() + isInteger so "90.5" is rejected here instead of round-tripping to a backend 422.
      const days = Number(this.form.days);
      if (!Number.isInteger(days) || days < 1 || days > MAX_DAYS) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000,
            this.$t('inactiveCustomers.error_days', {max: MAX_DAYS}));
        return null;
      }
      return days;
    },
    listParams(page) {
      return {
        days: this.form.days,
        include_never_opened: this.form.include_never_opened ? 1 : 0,
        province_id: this.form.province_id || undefined,
        customer_type: this.form.customer_type || undefined,
        q: (this.form.q || '').trim() || undefined,
        per_page: PER_PAGE,
        page,
      };
    },
    async loadProvinces() {
      try {
        this.provinces = await fetchProvinces();
      } catch (e) {
        this.provinces = [];
      }
    },
    async loadList(page = 1) {
      const days = this.validDays();
      if (!days) return;

      const requestId = ++this.listRequestId;
      this.loading = true;
      this.errorMessage = '';
      try {
        const data = await fetchInactiveCustomers(this.listParams(page));
        if (requestId !== this.listRequestId || !this.alive) return;
        this.rows = (data && data.rows) || [];
        this.paginator = (data && data.pagination) || null;
        this.cutoff = data && data.filter ? data.filter.cutoff : null;
        this.appliedDays = days;
      } catch (e) {
        if (requestId === this.listRequestId && this.alive) {
          this.rows = [];
          this.paginator = null;
          this.errorMessage = errorMessage(e, this.$t('inactiveCustomers.error_loading'));
        }
      } finally {
        if (requestId === this.listRequestId && this.alive) this.loading = false;
      }
    },
    setDays(days) {
      this.form.days = days;
      this.onFilterChange();
    },
    onFilterChange() {
      this.loadList(1);
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.loadList(1), 350);
    },
    changePage(page) {
      this.loadList(page);
    },
    fullName(row) {
      const name = [row.customer_name, row.customer_surname].filter(Boolean).join(' ');
      return name || this.placeholder;
    },
    address(row) {
      const parts = [row.village_name, row.district_name, row.province_name].filter(Boolean);
      return parts.length ? parts.join(', ') : this.placeholder;
    },
    formatDate(value) {
      return value ? this.moment(value).format('DD/MM/YYYY') : this.placeholder;
    },
    formatDateTime(value) {
      return value ? this.moment(value).format('DD/MM/YYYY HH:mm') : this.placeholder;
    },
    statusLabel(s) {
      return {
        0: this.$t('inactiveCustomers.exports.processing'),
        1: this.$t('inactiveCustomers.exports.ready'),
        2: this.$t('inactiveCustomers.exports.failed'),
      }[Number(s)] || s;
    },
    statusClass(s) {
      return {0: 'is-info', 1: 'is-success', 2: 'is-danger'}[Number(s)] || 'is-light';
    },
    async exportExcel() {
      const days = this.validDays();
      if (!days || this.requesting) return;

      this.requesting = true;
      try {
        const {per_page, page, ...filter} = this.listParams(1); // eslint-disable-line no-unused-vars
        await requestExport(filter);
        this.refreshCount = 0;
        this.$notification.OpenNotification_Error('top-right', 'success', 3000,
            this.$t('inactiveCustomers.exports.queued'));
        this.fetchExports();
      } catch (e) {
        const msg = e.response && e.response.status === 429
            ? this.$t('inactiveCustomers.exports.already_running')
            : errorMessage(e, this.$t('inactiveCustomers.error_export'));
        this.$notification.OpenNotification_Error('top-right', 'danger', 4000, msg);
      } finally {
        this.requesting = false;
      }
    },
    async fetchExports() {
      this.exportsLoading = true;
      try {
        const data = await listExports({per_page: 20});
        if (!this.alive) return;
        this.exports = (data && data.exports) || [];
        if (this.exports.some((f) => Number(f.status) === 0) && this.refreshCount < MAX_REFRESH) {
          this.scheduleRefresh();
        } else {
          this.stopRefresh();
        }
      } catch (e) {
        if (!this.alive) return;
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000,
            errorMessage(e, this.$t('inactiveCustomers.exports.error_list')));
      } finally {
        if (this.alive) this.exportsLoading = false;
      }
    },
    async download(id) {
      this.downloadingId = id;
      try {
        const result = await downloadExport(id);
        if (result && result.ready === false) {
          this.$notification.OpenNotification_Error('top-right', 'warning', 3000,
              this.$t('inactiveCustomers.exports.not_ready'));
          this.fetchExports();
        }
      } catch (e) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000,
            errorMessage(e, this.$t('inactiveCustomers.exports.error_download')));
      } finally {
        this.downloadingId = null;
      }
    },
    scheduleRefresh() {
      this.stopRefresh();
      if (!this.alive) return;
      const delay = Math.round(REFRESH_MS * (0.8 + Math.random() * 0.4)); // jitter
      this.refreshTimer = setTimeout(() => {
        this.refreshCount += 1;
        this.fetchExports();
      }, delay);
    },
    stopRefresh() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
  },
  created() {
    this.loadProvinces();
    this.loadList(1);
    this.fetchExports();
  },
  beforeDestroy() {
    this.alive = false;
    clearTimeout(this.searchDebounceTimer);
    this.stopRefresh();
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 40px;
}

.presets {
  margin-top: 6px;

  .tag {
    cursor: pointer;
    margin-right: 4px;
  }
}

.never-col {
  display: flex;
  align-items: flex-end;
  padding-bottom: 18px;
}

.hint {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
}

.state-panel {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  color: #555;
}

.error-panel {
  border-color: #f4b5b5;
  color: #b42318;
}

.exports-box {
  margin-top: 24px;

  .exports-title {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h5 {
      font-size: 16px;
      font-weight: 600;

      i {
        margin-right: 6px;
        color: #3273dc;
      }
    }
  }

  .reference {
    font-size: 13px;
    font-family: monospace;
    color: #444;
  }

  .py-3 {
    padding: 1.5rem 0;
  }
}
</style>
